/**
 * Ozon geometric logistics + target (port of calc_oz.py logistics_mode='new').
 * Pure functions — no DB. Bands/constants injected by caller.
 */

/** Default FULL_LOGISTIC bands from calc_oz.py (volume units = (h*w*l)/1e6). */
const DEFAULT_FULL_LOGISTIC_BANDS = [
    { min_vol: 0.0, max_vol: 0.2, value: 65.84 },
    { min_vol: 0.201, max_vol: 0.4, value: 75.2 },
    { min_vol: 0.401, max_vol: 0.6, value: 82.52 },
    { min_vol: 0.601, max_vol: 0.8, value: 87.99 },
    { min_vol: 0.801, max_vol: 1.0, value: 87.99 },
    { min_vol: 1.001, max_vol: 1.25, value: 93.49 },
    { min_vol: 1.251, max_vol: 1.5, value: 100.53 },
    { min_vol: 1.501, max_vol: 1.75, value: 101.58 },
    { min_vol: 1.751, max_vol: 2.0, value: 101.54 },
    { min_vol: 2.001, max_vol: 3.0, value: 107.82 },
    { min_vol: 3.001, max_vol: 4.0, value: 119.24 },
    { min_vol: 4.001, max_vol: 5.0, value: 131.24 },
    { min_vol: 5.001, max_vol: 6.0, value: 138.96 },
    { min_vol: 6.001, max_vol: 7.0, value: 162.16 },
    { min_vol: 7.001, max_vol: 8.0, value: 162.5 },
    { min_vol: 8.001, max_vol: 9.0, value: 171.76 },
    { min_vol: 9.001, max_vol: 10.0, value: 165.58 },
    { min_vol: 10.001, max_vol: 11.0, value: 179.45 },
    { min_vol: 11.001, max_vol: 12.0, value: 173.92 },
    { min_vol: 12.001, max_vol: 13.0, value: 167.11 },
    { min_vol: 13.001, max_vol: 14.0, value: 171.37 },
    { min_vol: 14.001, max_vol: 15.0, value: 190.25 },
    { min_vol: 15.001, max_vol: 17.0, value: 223.15 },
    { min_vol: 17.001, max_vol: 20.0, value: 241.73 },
    { min_vol: 20.001, max_vol: 25.0, value: 263.32 },
    { min_vol: 25.001, max_vol: 30.0, value: 304.49 },
    { min_vol: 30.001, max_vol: 35.0, value: 392.01 },
    { min_vol: 35.001, max_vol: 40.0, value: 369.01 },
    { min_vol: 40.001, max_vol: 45.0, value: 565.52 },
    { min_vol: 50.001, max_vol: 60.0, value: 772.74 },
    { min_vol: 60.001, max_vol: 70.0, value: 617.46 },
    { min_vol: 70.001, max_vol: 80.0, value: 744.4 },
    { min_vol: 80.001, max_vol: 90.0, value: 415.0 },
    { min_vol: 90.001, max_vol: 100.0, value: 894.56 },
    { min_vol: 100.001, max_vol: 125.0, value: 1293.23 },
    { min_vol: 125.001, max_vol: 150.0, value: 1551.98 },
    { min_vol: 150.001, max_vol: 175.0, value: 1777.24 },
    { min_vol: 175.001, max_vol: 200.0, value: 2061.43 },
    { min_vol: 200.001, max_vol: 400.0, value: 3149.9 },
    { min_vol: 400.001, max_vol: 600.0, value: 4700.96 },
    { min_vol: 600.001, max_vol: 800.0, value: 6166.45 },
    { min_vol: 800.001, max_vol: 999999999, value: 7388.25 },
];

/** 100% — запасной процент, с которым нельзя подтверждать товар. */
function isPlaceholderPurchasePercent(value) {
    const n = Number(value);
    return Number.isFinite(n) && n === 100;
}

function resolvePurchasePercent(productPct, nicheAvg) {
    const p = productPct != null ? Number(productPct) : 0;
    if (p > 0) return p;
    const n = nicheAvg != null ? Number(nicheAvg) : 0;
    if (n > 0) return n;
    return 100;
}

function getFullLogistic(volume, bands) {
    const list = bands && bands.length ? bands : DEFAULT_FULL_LOGISTIC_BANDS;
    for (let i = 0; i < list.length; i++) {
        const b = list[i];
        const minV = Number(b.min_vol);
        const maxV = Number(b.max_vol);
        const val = Number(b.value);
        if (volume >= minV && volume <= maxV) return val;
    }
    return null;
}

/** Geometric logistics per purchased unit (calc_oz.calc_ozon_logistics). purchasePercent is 0–100. */
function calcOzonLogistics(fullLogistic, purchasePercent, pvzReturnFee) {
    const p = Number(purchasePercent) / 100;
    if (!(p > 0)) throw new Error('purchase_percent must be > 0');
    const fee = pvzReturnFee != null ? Number(pvzReturnFee) : 15;
    const L = Number(fullLogistic);
    const forwardLogistic = L / p;
    const backLogistic = (L + fee) * (1 - p) / p;
    return {
        p,
        full_logistic: L,
        forward_logistic: forwardLogistic,
        back_logistic: backLogistic,
        logistic_total: forwardLogistic + backLogistic,
    };
}

/**
 * Full Ozon economics → cost_price + target (yuan).
 * @param {object} args
 * @param {number} args.h
 * @param {number} args.w
 * @param {number} args.l
 * @param {number} args.ozPriceSpp
 * @param {number} args.sppCoef - percent 0–100
 * @param {number} args.fbo - decimal 0–1
 * @param {number} args.purchasePercent - 0–100
 * @param {number} args.certRub
 * @param {object} args.constants - oz constants (decimals)
 * @param {Array<{min_vol,max_vol,value}>} [args.fullLogisticBands]
 */
function calcOzonEconomics(args) {
    const {
        h, w, l, ozPriceSpp, sppCoef, fbo, purchasePercent, certRub = 0, constants, fullLogisticBands,
    } = args;

    const volume = (Number(h) * Number(w) * Number(l)) / 1e6;
    const ozPrice = sppCoef
        ? Number(ozPriceSpp) / (1 - Number(sppCoef) / 100)
        : Number(ozPriceSpp);

    const fullLogistic = getFullLogistic(volume, fullLogisticBands);
    if (fullLogistic == null) {
        throw new Error(`full_logistic not defined: volume=${volume}`);
    }

    const fee = constants.pvz_return_fee != null ? constants.pvz_return_fee : 15;
    const logistics = calcOzonLogistics(fullLogistic, purchasePercent, fee);

    const truck = constants.truck_delivery_cost ?? 700;
    const customs = constants.customs_expenses_percent ?? 0;
    const logisticCn = volume * truck * (1 + customs);

    const irMarkup = constants.ir_markup_percent ?? 1.3;
    const tax = constants.profit_tax_percent ?? 0.25;
    const profitTaxFactor = irMarkup / (1 - tax);

    const unforeseen = constants.unforeseen_expenses_percent ?? 0;
    const advertisingPct = constants.advertising_budget_percent ?? 0.08;
    const acquiringPct = constants.acquiring_percent ?? 0.01;
    const scrapPct = constants.percent_scrap ?? 0.01;

    const fboDec = Number(fbo) > 1 ? Number(fbo) / 100 : Number(fbo);
    const revenueAfterFbo = ozPrice * (1 - fboDec) * (1 - unforeseen);
    const advertising = ozPrice * advertisingPct;
    const aquaring = ozPrice * acquiringPct;
    const scrap = ozPrice * scrapPct;

    const costPrice =
        (revenueAfterFbo - logistics.logistic_total - advertising - aquaring - scrap) /
        (1 + profitTaxFactor);

    const yuan = constants.yuan_exchange_rate ?? 11.2233;
    const vat = constants.vat_percent ?? 0.22;
    const duty = constants.duty_percent ?? 0.116;
    const bv = constants.bv_markup_percent ?? 0.15;
    const cert = Number(certRub) || 0;

    const priceBv =
        ((costPrice - logisticCn * yuan) / (1 + vat) / (1 + duty) - cert) / yuan;
    const targetPrice = priceBv * (1 - bv);

    return {
        volume,
        oz_price: ozPrice,
        fbo: fboDec,
        purchase_percent: Number(purchasePercent),
        ...logistics,
        logistic_cn: logisticCn,
        cert,
        revenue_after_fbo: revenueAfterFbo,
        advertising,
        aquaring,
        scrap,
        profit_tax_factor: profitTaxFactor,
        cost_price: costPrice,
        price_bv: priceBv,
        target_price: Math.round(targetPrice * 100) / 100,
        cost_price_rounded: Math.round(costPrice * 100) / 100,
    };
}

/** leftRun for markup: revenue after MP costs (same as numerator before / (1+profitTaxFactor)). */
function leftRunOzon(args) {
    const r = calcOzonEconomics(args);
    return r.revenue_after_fbo - r.logistic_total - r.advertising - r.aquaring - r.scrap;
}

export {
    DEFAULT_FULL_LOGISTIC_BANDS,
    isPlaceholderPurchasePercent,
    resolvePurchasePercent,
    getFullLogistic,
    calcOzonLogistics,
    calcOzonEconomics,
    leftRunOzon,
};
